import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, DollarSign, FileText, Hourglass, Loader2, MessageSquare, PhoneCall, Play, RefreshCw, Square, Wrench } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/apiAdmin";
import { HUD, fmtMoney, timeAgo } from "./theme";
import { useVoice } from "./useVoice";
import { RouteCard } from "./JarvisGoogle";

const Chip = ({ live, testid }) => (
  <span data-testid={testid} className={`${HUD.mono} text-[9px] tracking-[0.2em] px-2 py-0.5 rounded-full border ${live ? "border-emerald-400/40 text-emerald-300" : "border-amber-400/40 text-amber-300"}`}>
    {live ? "LIVE" : "STUB MODE"}
  </span>
);

const StatGrid = ({ stats }) => {
  const cards = [
    { k: "today_bookings", label: "Today's jobs", Icon: CalendarClock, color: "text-cyan-300" },
    { k: "in_progress", label: "In progress", Icon: Wrench, color: "text-violet-300" },
    { k: "pending", label: "Pending confirm", Icon: Hourglass, color: "text-amber-300" },
    { k: "unpaid", label: "Unpaid balances", Icon: FileText, color: "text-red-300" },
    { k: "drafts", label: "Drafts to approve", Icon: MessageSquare, color: "text-cyan-300" },
    { k: "revenue_today", label: "Collected today", Icon: DollarSign, color: "text-emerald-300", money: true },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {cards.map(({ k, label, Icon, color, money }, i) => (
        <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={`${HUD.panel} p-4`} data-testid={`jarvis-stat-${k}`}>
          <Icon className={`w-4 h-4 ${color} mb-3`} />
          <p className={`text-2xl font-['Outfit'] font-semibold ${color}`}>{money ? fmtMoney(stats?.[k]) : stats?.[k] ?? 0}</p>
          <p className={`${HUD.label} mt-1`}>{label}</p>
        </motion.div>
      ))}
    </div>
  );
};

const Briefing = ({ title }) => {
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const voice = useVoice();
  const load = async (refresh = false) => {
    setLoading(true);
    try {
      setB(await apiGet("/admin/jarvis/briefing", refresh ? { refresh: true } : {}));
    } catch (e) {
      toast.error("Briefing unavailable");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className={`${HUD.panel} p-5 relative overflow-hidden`} data-testid="jarvis-briefing">
      <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className={HUD.label}>Daily briefing</p>
        <div className="flex gap-1">
          <button onClick={() => (voice.speaking ? voice.hush() : voice.speak(b?.text))} disabled={!b} data-testid="jarvis-briefing-speak" className="p-2 rounded-full text-cyan-200 hover:bg-cyan-400/10">
            {voice.speaking ? <Square className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button onClick={() => load(true)} disabled={loading} data-testid="jarvis-briefing-refresh" className="p-2 rounded-full text-cyan-200 hover:bg-cyan-400/10">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {loading && !b ? (
        <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Compiling your briefing, {title}…</p>
      ) : (
        <p className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-wrap" data-testid="jarvis-briefing-text">{b?.text}</p>
      )}
    </div>
  );
};

const Integrations = ({ st }) => (
  <div className={`${HUD.panel} p-5 space-y-3`} data-testid="jarvis-integrations">
    <p className={HUD.label}>Integrations</p>
    {[
      [`AI · ${st?.integrations?.ai_model?.split(" — ")[0] || "core"}`, st?.integrations?.ai, "ai"],
      [`${st?.integrations?.voice_provider || "Vapi"} · phone calls`, st?.integrations?.vapi, "vapi"],
      ["OpenPhone · SMS", st?.integrations?.sms, "sms"],
    ].map(([label, live, k]) => (
      <div key={k} className="flex items-center justify-between text-sm text-gray-300">
        <span>{label}</span>
        <Chip live={live} testid={`jarvis-int-${k}`} />
      </div>
    ))}
    <p className="text-[11px] text-gray-500">Stub mode = every call / text is logged, nothing fake is ever sent.</p>
  </div>
);

const Team = ({ st }) => (
  <div className={`${HUD.panel} p-5 space-y-3`} data-testid="jarvis-team">
    <p className={HUD.label}>On-call · caller ID</p>
    {(st?.team || []).length === 0 ? (
      <p className="text-sm text-gray-500">No team numbers yet — add yours in JARVIS Settings so I recognise you when you call or text.</p>
    ) : (
      [...st.team].sort((a, b) => a.priority - b.priority).map((m) => (
        <div key={m.phone || m.name} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full border border-cyan-400/30 flex items-center justify-center text-cyan-200 text-xs">{(m.name || "?")[0]}</div>
          <div className="min-w-0">
            <p className="text-sm text-white truncate">{m.name} <span className="text-gray-500">· {m.role || "Team"}</span></p>
            <p className={`${HUD.mono} text-[10px] text-emerald-300/80`}>{m.phone} · P{m.priority}</p>
          </div>
        </div>
      ))
    )}
  </div>
);

const Activity = ({ tick }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { apiGet("/admin/jarvis/activity").then((d) => setItems(d.items || [])).catch(() => {}); }, [tick]);
  return (
    <div className={`${HUD.panel} p-5`} data-testid="jarvis-activity">
      <p className={`${HUD.label} mb-3`}>Latest activity</p>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500">Quiet so far.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.slice(0, 15).map((a) => (
            <p key={a.activity_id} className="text-xs text-gray-300 leading-relaxed">
              <span className={`${HUD.mono} text-cyan-300/70 mr-2`}>{timeAgo(a.created_at)}</span>{a.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export const JarvisCommand = ({ st, tick, onRefresh }) => {
  const [calling, setCalling] = useState(false);
  const callMe = async () => {
    setCalling(true);
    try {
      const r = await apiPost("/admin/jarvis/call", {});
      toast.success(r.status === "stub" ? "Briefing call logged (STUB) — connect Vapi to ring your phone" : `Calling ${r.name}…`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Call failed");
    } finally {
      setCalling(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={callMe} disabled={calling} data-testid="jarvis-briefing-call" className={`${HUD.ghost} h-9 px-4 text-sm flex items-center gap-2`}>
          {calling ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Call me with a briefing
        </button>
      </div>
      <StatGrid stats={st?.stats} />
      <Briefing title={st?.owner_title || "sir"} />
      <RouteCard />
      <div className="grid md:grid-cols-2 gap-4">
        <Integrations st={st} />
        <Team st={st} />
      </div>
      <Activity tick={tick} />
    </div>
  );
};

export default JarvisCommand;
